/**
 * Referral program cookie & link utilities.
 *
 * The referral code is passed via `?ref=CODE` and stored in a cookie so it
 * survives the signup → email-verification → MFA flow.
 */

export const REFERRAL_COOKIE_NAME = "app-referral-code";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

/** Read the referral code from a `Cookie` header string (server-side). */
export function readReferralCookie(cookieString: string | null): string | null {
  if (!cookieString) return null;
  const match = cookieString.match(
    new RegExp(`(?:^|;\\s*)${REFERRAL_COOKIE_NAME}=([^;]+)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
}

/** Write the referral code cookie (client-side). */
export function writeReferralCookie(code: string): void {
  if (typeof document === "undefined") return;
  document.cookie = `${REFERRAL_COOKIE_NAME}=${encodeURIComponent(
    code
  )}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

/** Clear the referral code cookie (client-side). */
export function clearReferralCookie(): void {
  if (typeof document === "undefined") return;
  document.cookie = `${REFERRAL_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
}

/**
 * Build a Set-Cookie header value for the referral code (server-side).
 * Append this to the Response headers.
 */
export function buildReferralSetCookieHeader(code: string): string {
  return `${REFERRAL_COOKIE_NAME}=${encodeURIComponent(
    code
  )}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

/** Build a full referral link from a code and domain. */
export function buildReferralLink(code: string, domain?: string): string {
  const base =
    domain || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}?ref=${encodeURIComponent(code)}`;
}

/** Discount per active premium referral (in NIS). */
export const REFERRAL_DISCOUNT_PER_FRIEND = 5;

/** Monthly base price (NIS). */
export const MONTHLY_BASE_PRICE = 49;

/** Yearly base price (NIS). */
export const YEARLY_BASE_PRICE = 489;
